/**
 * Scroll parallax on content that is already on the page (omega-parallax.js v3; docs/behavior/
 * hero-and-motion.md §6). Scoped to one page subtree instead of the document. Nothing is created:
 * only `translate`, `object-position`, `will-change` and `data-omega-depth` are written, and all of
 * them are removed on dispose.
 *
 * Differences from the overlay, all deliberate:
 * - the scan is limited to the page root handed in (the hero, nav, footer and drawers stay held);
 * - an image is registered once it has decoded, so it is classified pan/loose from its real crop
 *   (production classified whatever had loaded by its timed scans);
 * - the legacy `[data-parallax]` disarm and the Academy view poll are not ported (no legacy writer
 *   and no class-toggled views exist in the SPA).
 */
import {
  BLOCK_SEL,
  BOOT_MEASURE_MS,
  BOOT_SCANS_MS,
  CAP_BLOCK,
  CAP_CARD,
  CAP_HEAD,
  CAP_LOOSE,
  CAP_PAN,
  D_BLOCK,
  D_CARD,
  D_HEAD,
  D_IMG,
  GATE_SEL,
  GRID_SEL,
  HEAD_HEIGHT_RANGE,
  HEAD_SEL,
  HOLD_SEL,
  MEASURE_DEBOUNCE_MS,
  MIN_BLOCK_HEIGHT,
  MIN_CARD_HEIGHT,
  MIN_GAP,
  MIN_GAP_PLAIN,
  MIN_IMAGE_SIDE,
  MOBILE_K,
  PARALLAX_VERSION,
  REVEAL_SEL,
  SCAN_DEBOUNCE_MS,
} from './constants';
import { gapSide, neighbourGap, revealNear, type LayoutNode } from './gap';
import {
  classifyImage,
  coverCrop,
  finalAmplitude,
  isLive,
  neighbourSlackFor,
  objectPositionValue,
  oneWaySlack,
  panSlack,
  roundedOffset,
  scrollProgress,
  translateValue,
  wantedAmplitude,
  type LayerKind,
  type PanAxis,
} from './math';

export interface ParallaxLayer {
  readonly el: HTMLElement;
  readonly kind: LayerKind;
  readonly depth: number;
  readonly cap: number;
  readonly axis: PanAxis;
  readonly oneWay: boolean;
  amp: number;
  top: number;
  h: number;
  live: boolean;
  slack: number;
  last: number | null;
  src: string;
}

export interface ParallaxReportRow {
  readonly kind: LayerKind;
  readonly el: string;
  readonly ampPx: number;
  readonly slackPx: number;
  readonly bound: 'measured-slack' | 'cap' | 'ratio';
  readonly ratio: number;
  readonly src: string;
}

export interface OmegaParallaxHandle {
  readonly version: string;
  layers(): readonly ParallaxLayer[];
  measure(): void;
  release(): void;
  report(): ParallaxReportRow[];
}

declare global {
  interface Window {
    __omegaParallax?: OmegaParallaxHandle;
  }
}

function clips(node: LayoutNode): boolean {
  return node instanceof Element && window.getComputedStyle(node).overflow !== 'visible';
}

function imageCrop(el: HTMLElement) {
  const natural =
    el instanceof HTMLImageElement
      ? { w: el.naturalWidth, h: el.naturalHeight }
      : el instanceof HTMLVideoElement
        ? { w: el.videoWidth, h: el.videoHeight }
        : { w: 0, h: 0 };
  const r = el.getBoundingClientRect();
  return coverCrop(window.getComputedStyle(el).objectFit, natural.w, natural.h, r.width, r.height);
}

export class ParallaxController {
  private readonly root: HTMLElement;
  private readonly reduceMQ: MediaQueryList;
  private readonly mobileMQ: MediaQueryList;
  private layers: ParallaxLayer[] = [];
  private readonly registered = new WeakSet<Element>();
  private readonly grids = new WeakSet<Element>();
  private vh = window.innerHeight;
  private rafId = 0;
  private scanTimer = 0;
  private measureTimer = 0;
  private readonly bootTimers: number[] = [];
  private readonly cleanups: (() => void)[] = [];
  private disposed = false;

  constructor(root: HTMLElement) {
    this.root = root;
    this.reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.mobileMQ = window.matchMedia('(max-width: 768px)');
    document.documentElement.setAttribute('data-omega-parallax', 'v3');

    const onScroll = (): void => {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = 0;
        this.apply();
      });
    };
    const measureSoon = (): void => {
      this.scheduleMeasure();
    };
    const onVisibility = (): void => {
      this.cancelFrame();
      if (document.visibilityState === 'visible') this.scheduleMeasure();
    };
    const onFocus = (): void => {
      this.cancelFrame();
      this.scheduleMeasure();
    };
    const onImageLoad = (e: Event): void => {
      if (e.target instanceof HTMLImageElement) this.scheduleScan();
    };
    const onReduce = (): void => {
      if (this.reduceMQ.matches) this.release();
      else this.scan();
    };
    this.listen(window, 'scroll', onScroll, { passive: true });
    this.listen(window, 'resize', measureSoon, { passive: true });
    this.listen(window, 'orientationchange', measureSoon, { passive: true });
    this.listen(window, 'load', measureSoon);
    this.listen(window, 'pageshow', measureSoon);
    this.listen(document, 'visibilitychange', onVisibility);
    this.listen(window, 'focus', onFocus);
    this.listen(root, 'load', onImageLoad, true);
    this.listen(this.reduceMQ, 'change', onReduce);
    this.listen(this.mobileMQ, 'change', measureSoon);
    let fontsLive = true;
    void document.fonts?.ready.then(() => {
      if (fontsLive) this.scheduleMeasure();
    });
    this.cleanups.push(() => {
      fontsLive = false;
    });

    const observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.type === 'attributes' || record.addedNodes.length > 0) {
          this.scheduleScan();
          return;
        }
      }
    });
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
    this.cleanups.push(() => {
      observer.disconnect();
    });

    this.scan();
    for (const ms of BOOT_SCANS_MS) this.bootTimers.push(window.setTimeout(() => this.scan(), ms));
    this.bootTimers.push(window.setTimeout(() => this.scheduleMeasure(), BOOT_MEASURE_MS));
  }

  private listen(
    target: EventTarget,
    type: string,
    fn: EventListener,
    options?: AddEventListenerOptions | boolean,
  ): void {
    target.addEventListener(type, fn, options);
    this.cleanups.push(() => {
      target.removeEventListener(type, fn, options);
    });
  }

  private cancelFrame(): void {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  private taper(): number {
    return this.mobileMQ.matches ? MOBILE_K : 1;
  }

  private excluded(el: Element): boolean {
    return el.closest(GATE_SEL) !== null || el.closest(HOLD_SEL) !== null;
  }

  private register(el: HTMLElement, kind: LayerKind, depth: number, cap: number, axis: PanAxis = 'v'): void {
    if (this.registered.has(el) || this.excluded(el)) return;
    this.registered.add(el);
    el.setAttribute('data-omega-depth', kind);
    this.layers.push({
      el,
      kind,
      depth,
      cap,
      axis,
      oneWay: kind === 'pan' || kind === 'loose',
      amp: 0,
      top: 0,
      h: 0,
      live: false,
      slack: 0,
      last: null,
      src: '',
    });
  }

  private collectImages(): void {
    for (const im of Array.from(this.root.querySelectorAll<HTMLElement>('img,video'))) {
      if (this.registered.has(im) || this.excluded(im)) continue;
      if (im instanceof HTMLImageElement && !(im.complete && im.naturalWidth > 0)) continue;
      const r = im.getBoundingClientRect();
      if (r.height < MIN_IMAGE_SIDE || r.width < MIN_IMAGE_SIDE) continue;
      const cls = classifyImage(imageCrop(im));
      if (cls.kind === 'pan') this.register(im, 'pan', D_IMG, CAP_PAN, cls.axis);
      else this.register(im, 'loose', D_BLOCK, CAP_LOOSE);
    }
  }

  private collectGrids(): void {
    for (const g of Array.from(this.root.querySelectorAll<HTMLElement>(GRID_SEL))) {
      if (this.grids.has(g) || this.excluded(g)) continue;
      const gs = window.getComputedStyle(g);
      const gap = Number.parseFloat(gs.rowGap) || Number.parseFloat(gs.gap) || 0;
      if (gap < MIN_GAP_PLAIN) continue;
      const kids = Array.from(g.children);
      if (kids.some((k) => k.matches(REVEAL_SEL)) && gap < MIN_GAP) continue;
      this.grids.add(g);
      const cap = Math.min(CAP_CARD, gap * 0.45);
      kids.forEach((c, j) => {
        if (!(c instanceof HTMLElement) || this.registered.has(c)) return;
        if ((c.offsetHeight || 0) < MIN_CARD_HEIGHT) return;
        this.register(c, 'card', D_CARD * Math.sin(j * 1.7), cap);
      });
    }
  }

  private collectBlocks(): void {
    for (const n of Array.from(this.root.querySelectorAll<HTMLElement>(BLOCK_SEL))) {
      if (this.registered.has(n) || this.excluded(n)) continue;
      if ((n.offsetHeight || 0) < MIN_BLOCK_HEIGHT) continue;
      this.register(n, 'block', D_BLOCK, CAP_BLOCK);
    }
    for (const t of Array.from(this.root.querySelectorAll<HTMLElement>(HEAD_SEL))) {
      if (this.registered.has(t) || this.excluded(t)) continue;
      const th = t.offsetHeight || 0;
      if (th < HEAD_HEIGHT_RANGE[0] || th > HEAD_HEIGHT_RANGE[1]) continue;
      this.register(t, 'head', -D_HEAD, CAP_HEAD);
    }
  }

  scan(): void {
    if (this.disposed || this.reduceMQ.matches) return;
    this.collectImages();
    this.collectGrids();
    this.collectBlocks();
    this.measure();
  }

  private scheduleScan(): void {
    window.clearTimeout(this.scanTimer);
    this.scanTimer = window.setTimeout(() => {
      this.scan();
    }, SCAN_DEBOUNCE_MS);
  }

  private scheduleMeasure(): void {
    window.clearTimeout(this.measureTimer);
    this.measureTimer = window.setTimeout(() => {
      this.measure();
    }, MEASURE_DEBOUNCE_MS);
  }

  measure(): void {
    if (this.disposed) return;
    this.vh = window.innerHeight;
    const sy = window.pageYOffset || document.documentElement.scrollTop || 0;
    const k = this.taper();
    this.layers = this.layers.filter((L) => L.el.isConnected);
    for (const L of this.layers) {
      const r0 = L.el.getBoundingClientRect();
      const ourY = L.kind !== 'pan' && typeof L.last === 'number' ? L.last : 0;
      L.h = L.el.offsetHeight || 0 || r0.height;
      L.top = r0.top + sy - ourY;
      if (L.h < 1) {
        L.amp = 0;
        continue;
      }
      const want = wantedAmplitude(L.depth, this.vh, L.h, k, L.cap);
      if (L.kind === 'pan') {
        L.slack = panSlack(imageCrop(L.el), L.axis);
        L.src = 'cover-crop ' + L.axis;
      } else if (L.oneWay) {
        L.slack = oneWaySlack(gapSide(L.el, -1, clips));
        L.src = 'gap-above';
      } else {
        const gap = neighbourGap(L.el, clips);
        const near = revealNear(L.el);
        L.slack = neighbourSlackFor(gap, near);
        const need = near ? MIN_GAP : MIN_GAP_PLAIN;
        L.src = 'gap ' + Math.round(gap) + 'px' + (gap < need ? ' < ' + need + 'px needed' : '');
      }
      L.amp = finalAmplitude(want, L.slack, L.depth);
      L.last = null;
    }
    this.apply();
  }

  apply(): void {
    if (this.disposed) return;
    const sy = window.pageYOffset || document.documentElement.scrollTop || 0;
    for (const L of this.layers) {
      const style = L.el.style;
      if (!L.amp) {
        if (L.live || L.last !== null) {
          L.live = false;
          L.last = null;
          style.translate = '';
          style.objectPosition = '';
          style.willChange = '';
        }
        continue;
      }
      const onscreen = isLive(L.top, L.h, sy, this.vh);
      if (onscreen !== L.live) {
        L.live = onscreen;
        style.willChange = onscreen ? (L.kind === 'pan' ? 'object-position' : 'translate') : '';
        if (!onscreen) {
          L.last = null;
          style.translate = '';
          style.objectPosition = '';
        }
      }
      if (!onscreen) continue;
      const p = scrollProgress(L.top, L.h, sy, this.vh, L.oneWay);
      const n = roundedOffset(p, L.amp, L.kind);
      if (n === L.last) continue;
      L.last = n;
      if (L.kind === 'pan') style.objectPosition = objectPositionValue(n, L.axis);
      else style.translate = translateValue(n);
    }
  }

  /** Clears every offset (reduced motion switched on); layers stay registered. */
  release(): void {
    for (const L of this.layers) {
      L.live = false;
      L.last = null;
      L.el.style.translate = '';
      L.el.style.scale = '';
      L.el.style.objectPosition = '';
      L.el.style.willChange = '';
    }
  }

  layersSnapshot(): readonly ParallaxLayer[] {
    return this.layers;
  }

  report(): ParallaxReportRow[] {
    return this.layers.map((L) => ({
      kind: L.kind,
      el: (L.el.tagName + '.' + String(L.el.className || '')).slice(0, 46),
      ampPx: Math.round(L.amp * 100) / 100,
      slackPx: Math.round(L.slack * 10) / 10,
      bound: Math.abs(L.amp) >= L.slack - 0.6 ? 'measured-slack' : Math.abs(L.amp) >= L.cap - 0.6 ? 'cap' : 'ratio',
      ratio: L.h ? Math.round(((Math.abs(L.amp) * 2) / (this.vh + L.h)) * 1000) / 10 : 0,
      src: L.src,
    }));
  }

  handle(): OmegaParallaxHandle {
    return {
      version: PARALLAX_VERSION,
      layers: () => this.layersSnapshot(),
      measure: () => {
        this.measure();
      },
      release: () => {
        this.release();
      },
      report: () => this.report(),
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.release();
    for (const L of this.layers) L.el.removeAttribute('data-omega-depth');
    this.layers = [];
    this.disposed = true;
    this.cancelFrame();
    window.clearTimeout(this.scanTimer);
    window.clearTimeout(this.measureTimer);
    for (const id of this.bootTimers) window.clearTimeout(id);
    for (const cleanup of this.cleanups) cleanup();
    this.cleanups.length = 0;
  }
}
